import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Toggle/Switch component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 177:1416):
 * - Track Size: 36x24px (20px inner)
 * - Thumb Size: 16x16px
 * - Border Radius: 12px track, 99px thumb
 * - Off: Grey-300 bg (#D4D4D8)
 * - On: Grey-950 bg (#09090B)
 * - Focus: 4px ring, 1px white inner border
 */
const toggleVariants = cva(
  'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-grey-950/10 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-grey-300 data-[state=checked]:bg-grey-950 dark:bg-grey-700 dark:data-[state=checked]:bg-grey-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toggle = React.forwardRef(({ className, variant, ...props }, ref) => (
  <SwitchPrimitive.Root
    className={cn(toggleVariants({ variant }), className)}
    {...props}
    ref={ref}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'pointer-events-none block h-4 w-4 rounded-full bg-white shadow-[0px_1px_2px_0px_rgba(18,18,18,0.2),inset_0px_1px_2px_1px_rgba(18,18,18,0.15)] transition-all duration-200',
        'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        // Dark mode: white thumb on grey-700 track (unchecked), dark thumb on grey-50 track (checked)
        'dark:bg-grey-300 dark:data-[state=checked]:bg-grey-950'
      )}
    />
  </SwitchPrimitive.Root>
));
Toggle.displayName = SwitchPrimitive.Root.displayName;

// Alias for semantic naming
const Switch = Toggle;

/**
 * ToggleField - Toggle with label and optional hint text
 */
const ToggleField = React.forwardRef(
  ({ className, label, hint, id, ...props }, ref) => {
    const autoId = React.useId();
    const generatedId = id || autoId;
    return (
      <div className={cn('flex items-center justify-between gap-4', className)}>
        <div className="flex flex-col gap-0.5">
          <label
            htmlFor={generatedId}
            className="text-sm font-medium leading-5 text-grey-950 cursor-pointer dark:text-grey-50"
          >
            {label}
          </label>
          {hint && (
            <p className="text-xs text-grey-500 leading-4">{hint}</p>
          )}
        </div>
        <Toggle ref={ref} id={generatedId} {...props} />
      </div>
    );
  }
);
ToggleField.displayName = 'ToggleField';

export { Toggle, Switch, ToggleField, toggleVariants };
